import type { LinkPreviewDto, PrivacyDto } from '@dijitalapp/shared';
import { useEffect, useSyncExternalStore } from 'react';
import { api } from './api';
import { safeThumb } from './mediaThumb';

/*
 * Link previews in encrypted chats. The sender's device has our server read the page (the same reader as in
 * other chats), shrinks the picture, and puts title, description, site and picture inside the encrypted
 * message. The recipient's device fetches nothing: it shows what arrived, like WhatsApp. Each person opts in
 * (Privacy settings), because the server then learns which links they share.
 */

export interface EncryptedLinkCard {
  url: string;
  title: string | null;
  description: string | null;
  site: string | null;
  /** A small JPEG data URL, or null. */
  thumb: string | null;
}

let on: boolean | null = null;
const listeners = new Set<() => void>();
let loading = false;

export function setEncryptedPreviewsPref(value: boolean): void {
  on = value;
  listeners.forEach((l) => l());
}

/** The person's choice (Privacy › link previews in encrypted chats); false until known. */
export function useEncryptedPreviewsPref(): boolean {
  const value = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => on,
  );
  useEffect(() => {
    if (on !== null || loading) return;
    loading = true;
    api<PrivacyDto>('GET', '/me/privacy').then(
      (r) => setEncryptedPreviewsPref(Boolean(r.settings.encryptedLinkPreviews)),
      () => undefined,
    ).finally(() => {
      loading = false;
    });
  }, []);
  return Boolean(value);
}

/** The picture of a server preview, made small enough to travel inside a message (≤ 480 px JPEG). */
async function shrink(previewId: string): Promise<string | null> {
  try {
    const res = await fetch(`/api/v1/link-previews/${previewId}/image`, { credentials: 'same-origin' });
    if (!res.ok) return null;
    const bitmap = await createImageBitmap(await res.blob());
    const k = Math.min(1, 480 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * k));
    canvas.height = Math.max(1, Math.round(bitmap.height * k));
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    return safeThumb(canvas.toDataURL('image/jpeg', 0.7));
  } catch {
    return null;
  }
}

export async function cardFromPreview(p: LinkPreviewDto): Promise<EncryptedLinkCard> {
  return {
    url: p.url,
    title: p.title?.slice(0, 300) ?? null,
    description: p.description?.slice(0, 400) ?? null,
    site: p.siteName?.slice(0, 120) ?? null,
    thumb: p.hasImage ? await shrink(p.id) : null,
  };
}

/** What arrived in a message is checked before it is shown: plain strings, an http(s) link, a safe picture. */
export function readLinkCard(value: unknown): EncryptedLinkCard | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const url = typeof v.url === 'string' && /^https?:\/\//i.test(v.url) && URL.canParse(v.url) ? v.url : null;
  if (!url) return null;
  const text = (x: unknown, max: number) => (typeof x === 'string' && x.trim() ? x.slice(0, max) : null);
  return { url, title: text(v.title, 300), description: text(v.description, 400), site: text(v.site, 120), thumb: safeThumb(v.thumb) };
}
